import { Routes } from '@angular/router';
import { provideState } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { RemoteHtmlViewerComponent } from './shared/components/remote-html-viewer/remote-html-viewer.component';
import { environment } from '@env/environment';
import { Tab } from '@components/footer/tabs';

import * as collection from './modules/collection/store/collection.reducers';
import * as notification from './modules/notification/store/notification.reducers';
import * as news from './modules/news/store/news.reducers';
import * as profile from './modules/profile/store/profile.reducers';
import * as location from './modules/location/store/location.reducers';
import * as collectionItem from './modules/collection-item/store/collection-item.reducers';

import { CollectionEffects } from './modules/collection/store/collection.effects';
import { NewsEffects } from './modules/news/store/news.effects';
import { NotificationEffects } from './modules/notification/store/notification.effects';
import { LocationEffects } from './modules/location/store/location.effects';
import { CollectionItemEffects } from './modules/collection-item/store/collection-item.effects';

import { authenticatedUser } from './core/guards/authentication.guard';
import { loginRedirectGuard } from './core/guards/login-redirect.guard';
import { signupValidationGuard } from './core/guards/signup-validation.guard';
import { CarouselComponent } from './modules/authentication/pages/get-started-old/carousel/carousel.component';

export const routes: Routes = [
  {
    path: '',
    loadChildren: () =>
      import('./modules/authentication/authentication.routes').then(
        (m) => m.authenticationRoutes,
      ),
    canActivate: [loginRedirectGuard],
    data: {
      tab: Tab.Auth,
      hideHeader: true,
      hideFooter: true,
    },
  },
  {
    path: 'about',
    component: RemoteHtmlViewerComponent,
    data: {
      url: environment.landingLink + '/about-us',
      clean: true,
      headerVariant: 'back',
      headerTitle: 'About',
      headerTitleRaw: false,
    },
  },
  {
    path: 'help',
    component: CarouselComponent,
    data: {
      isFromProfile: true,
      headerVariant: 'back',
      headerTitle: 'Get started',
      headerTitleRaw: false,
    },
  },
  {
    path: 'sign-up/validation',
    loadComponent: () =>
      import(
        './modules/authentication/pages/sign-up/pages/validation/validation.component'
        ).then((m) => m.ValidationComponent),
    canActivate: [signupValidationGuard],
    data: {
      hideHeader: true,
      hideFooter: true,
    },
  },
  {
    path: 'tabs',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'collection',
      },
      {
        path: 'news',
        loadChildren: () =>
          import('./modules/news/news.routes').then((m) => m.newsRoutes),
        providers: [
          provideState(news.newsFeatureKey, news.newsReducer),
          provideEffects([NewsEffects]),
        ],
        data: {
          tab: Tab.News,
          background: true,
          headerVariant: 'brand',
        },
      },
      {
        path: 'collection',
        canActivate: [authenticatedUser],
        loadChildren: () =>
          import('./modules/collection/collection.routes').then(
            (m) => m.collectionRoutes,
          ),
        providers: [
          provideState(
            collection.collectionFeatureKey,
            collection.collectionReducer,
          ),
          provideEffects([CollectionEffects]),
        ],
        data: {
          tab: Tab.Collection,
          background: true,
          headerVariant: 'brand',
        },
      },
      {
        path: 'notification',
        canActivate: [authenticatedUser],
        loadChildren: () =>
          import('./modules/notification/notification.routes').then(
            (m) => m.notificationRoutes,
          ),
        providers: [
          provideState(
            notification.notificationFeatureKey,
            notification.notificationReducer,
          ),
          provideEffects([NotificationEffects]),
        ],
        data: {
          tab: Tab.Notification,
          background: true,
          headerVariant: 'brand',
        },
      },
      {
        path: 'profile',
        canActivate: [authenticatedUser],
        loadChildren: () =>
          import('./modules/profile/profile.routes').then(
            (m) => m.profileRoutes,
          ),
        providers: [
          provideState(profile.profileFeatureKey, profile.profileReducer),
        ],
        data: {
          tab: Tab.Profile,
          background: true,
          headerVariant: 'brand',
        },
      },
      {
        path: 'location',
        canActivate: [authenticatedUser],
        loadChildren: () =>
          import('./modules/location/location.routes').then(
            (m) => m.locationRoutes,
          ),
        providers: [
          provideState(location.locationFeatureKey, location.locationReducer),
          provideEffects([LocationEffects]),
        ],
        data: {
          tab: Tab.Location,
          hideFooter: false,
        },
      },
      {
        path: 'scan',
        loadComponent: () =>
          import('./modules/collection/pages/scan/scan.component').then(
            (m) => m.ScanComponent,
          ),
        providers: [
          provideState(
            collectionItem.collectionItemFeatureKey,
            collectionItem.collectionItemReducer,
          ),
          provideEffects([CollectionEffects]),
        ],
        data: {
          hideHeader: true,
          hideFooter: true,
          background: false,
        },
      },
      {
        path: 'collection-item',
        loadChildren: () =>
          import('./modules/collection-item/collection-item.routes').then(
            (m) => m.collectionItemRoutes,
          ),
        providers: [
          provideState(
            collectionItem.collectionItemFeatureKey,
            collectionItem.collectionItemReducer,
          ),
          provideEffects([CollectionItemEffects]),
        ],
        data: {
          tab: Tab.CollectionItem,
          hideFooter: false,
          background: false,
          headerVariant: 'close',
        },
      },
    ],
  },
  {
    path: 'no-scan',
    loadComponent: () =>
      import('./modules/collection/pages/no-scan/no-scan.component').then(
        (m) => m.NoScanComponent,
      ),
    data: {
      hideHeader: true,
      hideFooter: true,
    },
  },
  {
    path: '**',
    redirectTo: 'tabs',
  },
];
